import { stringify } from 'csv-stringify/browser/esm/sync';
import { format } from 'date-fns';

import { RecognitionCount } from '../../../bonus-feed/types/user';

const SUMMARY_ROW_COUNT = 5;

export interface LeaderboardSummarySection {
	key: string;
	nameHeader: string;
	countHeader: string;
	users: RecognitionCount[];
	hashtag?: string | null;
}

export interface LeaderboardSummaryCsvLabels {
	overallMostRecognized: string;
	overallTopGivers: string;
	recognitionReceived: string;
	recognitionGiven: string;
	hashtagReceivers: (hashtag: string) => string;
	hashtagGivers: (hashtag: string) => string;
}

export const getExportUserName = (user: RecognitionCount['user']): string => {
	const preferred = (user as RecognitionCount['user'] & { preferred_first_name?: string | null }).preferred_first_name;
	const firstName = preferred?.trim() || user.first_name;
	const lastName = user.last_name?.trim();
	return [firstName, lastName].filter(Boolean).join(' ');
};

export const getCountForRow = (record: RecognitionCount, hashtag?: string | null): number => {
	const hashtagKey = hashtag?.replace(/^#/, '');
	if (hashtagKey && record.counts[hashtagKey] !== undefined) {
		return record.counts[hashtagKey];
	}
	return record.counts.total ?? 0;
};

export const buildLeaderboardSummarySections = (
	labels: LeaderboardSummaryCsvLabels,
	receivedUsers: RecognitionCount[],
	givenUsers: RecognitionCount[],
	hashtagReceivers: Record<string, RecognitionCount[]>,
	hashtagGivers: Record<string, RecognitionCount[]>,
	hashtags: string[]
): LeaderboardSummarySection[] => {
	const sections: LeaderboardSummarySection[] = [
		{
			key: 'overall-received',
			nameHeader: labels.overallMostRecognized,
			countHeader: labels.recognitionReceived,
			users: receivedUsers,
		},
		{
			key: 'overall-given',
			nameHeader: labels.overallTopGivers,
			countHeader: labels.recognitionGiven,
			users: givenUsers,
		},
	];

	for (const hashtag of hashtags) {
		const hashtagValue = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
		const hashtagKey = hashtagValue.replace(/^#/, '');

		sections.push({
			key: `${hashtagKey}-received`,
			nameHeader: labels.hashtagReceivers(hashtagValue),
			countHeader: labels.recognitionReceived,
			users: hashtagReceivers[hashtagKey] ?? [],
			hashtag: hashtagValue,
		});
		sections.push({
			key: `${hashtagKey}-given`,
			nameHeader: labels.hashtagGivers(hashtagValue),
			countHeader: labels.recognitionGiven,
			users: hashtagGivers[hashtagKey] ?? [],
			hashtag: hashtagValue,
		});
	}

	return sections;
};

export const buildLeaderboardSummaryCsv = (sections: LeaderboardSummarySection[]): string => {
	const headers = sections.flatMap((section) => [section.nameHeader, section.countHeader]);

	const rows = Array.from({ length: SUMMARY_ROW_COUNT }, (_, rankIndex) => {
		return sections.flatMap((section) => {
			const record = section.users[rankIndex];
			if (!record) return ['', ''];
			return [getExportUserName(record.user), String(getCountForRow(record, section.hashtag))];
		});
	});

	return stringify(rows, {
		columns: headers,
		header: true,
	});
};

export const triggerCsvDownload = (csv: string, fileName: string) => {
	const blob = new Blob([csv], { type: 'text/csv' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${fileName}.csv`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

export const downloadLeaderboardSummaryCsv = (csv: string, startDate: string, endDate: string) => {
	const fileName = `leaderboard-summary-${format(new Date(startDate), 'yyyy-MM-dd')}-to-${format(new Date(endDate), 'yyyy-MM-dd')}`;
	triggerCsvDownload(csv, fileName);
};
